import { SmallAddIcon } from "@chakra-ui/icons";
import {
  Button,
  HStack,
  Link,
  MenuItem,
  Radio,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import Decimal from "decimal.js";
import FeeTable, { FeeTableGetFeeEditPathFn } from "features/fees/FeeTable";
import { FundFamilySetupTabProps } from "features/fund-family/setup/FundFamilySetupTabProps";
import {
  getFeeWaiverConfigRetrieveQueryKey,
  useFeeWaiverConfigRetrieveSuspense,
  useFeeWaiverConfigUpdate,
} from "gen/endpoints/fee-waiver-config/fee-waiver-config";
import { useFundListSuspense } from "gen/endpoints/fund-entities/fund-entities";
import {
  getFundFamilyConfigRetrieveQueryKey,
  useFundFamilyConfigRetrieveSuspense,
  useFundFamilyConfigUpdate,
} from "gen/endpoints/fund-family-config/fund-family-config";
import {
  useTransactionCalculationListSuspense,
  useTransactionCalculationRetrieve,
} from "gen/endpoints/transactions/transactions";
import { FeeInterestMethodologyEnum } from "gen/model/feeInterestMethodologyEnum";
import { FeeWaiverCalculationFundEntityRelationUpsertData } from "gen/model/feeWaiverCalculationFundEntityRelationUpsertData";
import { FeeWaiverConfigUpsertDataRequest } from "gen/model/feeWaiverConfigUpsertDataRequest";
import { FundFamilyConfigReadData } from "gen/model/fundFamilyConfigReadData";
import { FundFamilyConfigUpsertDataRequest } from "gen/model/fundFamilyConfigUpsertDataRequest";
import { InterestDayCountEnum } from "gen/model/interestDayCountEnum";
import { ManagementFeeCallMethodologyEnum } from "gen/model/managementFeeCallMethodologyEnum";
import { ManagementFeeDistributionMethodologyEnum } from "gen/model/managementFeeDistributionMethodologyEnum";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { feeWaiverConfigUpdateBody } from "gen/schemas/fee-waiver-config/fee-waiver-config";
import { fundFamilyConfigUpdateBody } from "gen/schemas/fund-family-config/fund-family-config";
import React, { useCallback, useMemo } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Restricted } from "shared/components/access/Restricted";
import { FormBooleanToggleButtonGroup } from "shared/components/button/ToggleButton";
import {
  ContentBlock,
  ContentBlockActions,
  ContentBlockBody,
  ContentBlockGroup,
  ContentBlockHeader,
  ContentBlockMenu,
  ContentBlockTitle,
} from "shared/components/content/ContentBlock";
import Descriptions, {
  DescriptionsItemProps,
} from "shared/components/descriptions/Descriptions";
import { FormDrawer } from "shared/components/form/FormDrawer";
import { FormFieldPrefix } from "shared/components/form/FormFieldPrefix";
import { FormFieldset } from "shared/components/form/FormFieldset";
import { FormDateInput } from "shared/components/input/date/DateInput";
import { FormFundEntitySelect } from "shared/components/input/select/EntitySelect";
import { FormSelect } from "shared/components/input/select/Select";
import { SelectOption } from "shared/components/input/select/SelectOption";
import { TransactionCalculationSelect } from "shared/components/input/select/TransactionCalculationSelect";
import { FormNumberInput } from "shared/components/input/text/NumberInput";
import { PageTabBody } from "shared/components/layout/PageTabBody";
import { FormRadioGroup } from "shared/components/radio/FormRadio";
import { rootRoute } from "shared/navigation/Routes";
import { formatIsoDateToDisplay } from "shared/utils/DateUtils";
import { formatPercentage } from "shared/utils/formattingUtils";

export const FundFamilySetupFundTabFees: React.FC = () => {
  const { fundFamilyId } = useOutletContext<FundFamilySetupTabProps>();

  const { data: fundFamilyConfig } =
    useFundFamilyConfigRetrieveSuspense(fundFamilyId);

  const baseRoute = useMemo(
    () =>
      rootRoute({})
        .fundFamilies({})
        .fundFamily({ fundFamilyId })
        .setup({})
        .fund({})
        .fees({}),
    [fundFamilyId],
  );

  const getFeeEditPath = useCallback<FeeTableGetFeeEditPathFn>(
    (feeId) => baseRoute.view({ feeId }).$,
    [baseRoute],
  );

  return (
    <PageTabBody>
      <FeeTable
        fundFamilyId={fundFamilyId}
        retrieveParams={{}}
        getFeeEditPath={getFeeEditPath}
        showTimeline={true}
      />
      <ContentBlockGroup>
        <FundFamilyFeeWaiverConfigBlock
          fundFamilyConfig={fundFamilyConfig.config}
        />
        <FundFamilyFeeConfigBlock fundFamilyConfig={fundFamilyConfig.config} />
        <FundFamilyFeeTrueUpsConfigBlock
          fundFamilyConfig={fundFamilyConfig.config}
        />
        {fundFamilyConfig.config.enable_management_fee_true_ups && (
          <FundFamilyFeeInterestConfigBlock
            fundFamilyConfig={fundFamilyConfig.config}
          />
        )}
      </ContentBlockGroup>
    </PageTabBody>
  );
};

const FundFamilyFeeConfigBlock: React.FC<{
  fundFamilyConfig: FundFamilyConfigReadData;
}> = ({ fundFamilyConfig }) => {
  const { t } = useTranslation(["fundFamily", "common"]);
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <>
      <ContentBlock>
        <ContentBlockHeader>
          <ContentBlockTitle>
            {t("fundFamily:config.managementFeeConfig.title")}
          </ContentBlockTitle>
          <Restricted permission={PermissionTypeEnum.config}>
            <ContentBlockActions>
              <ContentBlockMenu>
                <MenuItem onClick={onOpen}>{t("common:edit")}</MenuItem>
              </ContentBlockMenu>
            </ContentBlockActions>
          </Restricted>
        </ContentBlockHeader>

        <ContentBlockBody variant="descriptions">
          <Descriptions>
            <Descriptions.Item
              label={t(
                "fundFamily:config.managementFeeConfig.capital_call.title",
              )}
            >
              {t(
                `fundFamily:config.managementFeeConfig.capital_call.${fundFamilyConfig.management_fee_call_methodology}.title`,
              )}
            </Descriptions.Item>
            <Descriptions.Item
              label={t(
                "fundFamily:config.managementFeeConfig.distribution.title",
              )}
            >
              {t(
                `fundFamily:config.managementFeeConfig.distribution.${fundFamilyConfig.management_fee_distribution_methodology}.title`,
              )}
            </Descriptions.Item>
          </Descriptions>
        </ContentBlockBody>
      </ContentBlock>
      {isOpen && (
        <ManagementFeeEditDrawer
          fundFamilyId={fundFamilyConfig.fund_family_id}
          fundFamilyConfig={fundFamilyConfig}
          onCloseComplete={onClose}
        />
      )}
    </>
  );
};

const FundFamilyFeeWaiverConfigBlock: React.FC<{
  fundFamilyConfig: FundFamilyConfigReadData;
}> = ({ fundFamilyConfig }) => {
  const { t } = useTranslation(["fundFamily", "common"]);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();

  const { data: feeWaiverConfig } = useFeeWaiverConfigRetrieveSuspense(
    fundFamilyConfig.fund_family_id,
  );

  const fundEntityIds = feeWaiverConfig.fee_waiver_calculations.map(
    (calc) => calc.fund_entity_id,
  );

  const { data: funds } = useFundListSuspense({ fund_ids: fundEntityIds });

  const { data: feeWaiverCalc } = useTransactionCalculationRetrieve(
    fundFamilyConfig.fund_family_id,
    feeWaiverConfig.fee_waiver_calculation_id!,
    {
      query: {
        enabled: !!feeWaiverConfig.fee_waiver_calculation_id,
      },
    },
  );

  const { data: feeWaiverCapCalc } = useTransactionCalculationRetrieve(
    fundFamilyConfig.fund_family_id,
    feeWaiverConfig.fee_waiver_cap_calculation_id!,
    {
      query: {
        enabled: !!feeWaiverConfig.fee_waiver_cap_calculation_id,
      },
    },
  );

  const { data: transactionCalculations } =
    useTransactionCalculationListSuspense(fundFamilyConfig.fund_family_id);

  const feeWaiverCalculations: Array<
    React.ReactElement<DescriptionsItemProps>
  > = feeWaiverConfig.fee_waiver_calculations.map((calc) => {
    const fundEntity = funds.find((fund) => fund.id === calc.fund_entity_id);
    const transactionCalculation = transactionCalculations.find(
      (tc) => tc.id === calc.fee_waiver_calculation_id,
    );

    return (
      <Descriptions.Item label={fundEntity?.legal_name} key={fundEntity?.id}>
        <Link
          onClick={() =>
            navigate(getCalculationEditPath(calc.fee_waiver_calculation_id))
          }
        >
          {transactionCalculation!.name}
        </Link>
      </Descriptions.Item>
    );
  });

  const getCalculationEditPath = useCallback(
    (transactionCalculationId: string) =>
      rootRoute({})
        .fundFamilies({})
        .fundFamily({
          fundFamilyId: fundFamilyConfig.fund_family_id,
        })
        .setup({})
        .fund({})
        .calculationEdit({
          transactionCalculationId,
        }).$,
    [fundFamilyConfig.fund_family_id],
  );

  return (
    <>
      <ContentBlock>
        <ContentBlockHeader>
          <ContentBlockTitle>
            {t("fundFamily:config.feeWaiverConfig.title")}
          </ContentBlockTitle>
          <Restricted permission={PermissionTypeEnum.config}>
            <ContentBlockActions>
              <ContentBlockMenu>
                <MenuItem onClick={onOpen}>{t("common:edit")}</MenuItem>
              </ContentBlockMenu>
            </ContentBlockActions>
          </Restricted>
        </ContentBlockHeader>
        <ContentBlockBody variant="descriptions">
          <Descriptions>
            <Descriptions.Item
              label={t("fundFamily:config.feeWaiverConfig.isEnabled")}
            >
              {feeWaiverConfig.is_enabled ? t("common:yes") : t("common:no")}
            </Descriptions.Item>
            <Descriptions.Item
              label={t("fundFamily:config.feeWaiverConfig.startDate")}
            >
              {formatIsoDateToDisplay(feeWaiverConfig.start_date)}
            </Descriptions.Item>
            <Descriptions.Item
              label={t("fundFamily:config.feeWaiverConfig.calculation")}
            >
              {feeWaiverCalc?.transaction_calculation && (
                <Link
                  onClick={() =>
                    navigate(
                      getCalculationEditPath(
                        feeWaiverCalc.transaction_calculation.id,
                      ),
                    )
                  }
                >
                  {feeWaiverCalc.transaction_calculation.name}
                </Link>
              )}
            </Descriptions.Item>
            <Descriptions.Item
              label={t("fundFamily:config.feeWaiverConfig.capCalculation")}
            >
              {feeWaiverCapCalc?.transaction_calculation && (
                <Link
                  onClick={() =>
                    navigate(
                      getCalculationEditPath(
                        feeWaiverCapCalc.transaction_calculation.id,
                      ),
                    )
                  }
                >
                  {feeWaiverCapCalc.transaction_calculation.name}
                </Link>
              )}
            </Descriptions.Item>
            {feeWaiverCalculations}
          </Descriptions>
        </ContentBlockBody>
      </ContentBlock>
      {isOpen && (
        <FeeWaiverEditDrawer
          fundFamilyId={fundFamilyConfig.fund_family_id}
          onCloseComplete={onClose}
        />
      )}
    </>
  );
};

const FundFamilyFeeTrueUpsConfigBlock: React.FC<{
  fundFamilyConfig: FundFamilyConfigReadData;
}> = ({ fundFamilyConfig }) => {
  const { t } = useTranslation(["fundFamily", "common"]);
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <>
      <ContentBlock>
        <ContentBlockHeader>
          <ContentBlockTitle>
            {t("fundFamily:config.managementFeeTrueUpConfig.title")}
          </ContentBlockTitle>
          <Restricted permission={PermissionTypeEnum.config}>
            <ContentBlockActions>
              <ContentBlockMenu>
                <MenuItem onClick={onOpen}>{t("common:edit")}</MenuItem>
              </ContentBlockMenu>
            </ContentBlockActions>
          </Restricted>
        </ContentBlockHeader>

        <ContentBlockBody variant="descriptions">
          <Descriptions>
            <Descriptions.Item
              label={t(
                "fundFamily:setup.profile.advanced.managementFeeTrueUps",
              )}
            >
              {fundFamilyConfig.enable_management_fee_true_ups
                ? t("common:yes")
                : t("common:no")}
            </Descriptions.Item>
          </Descriptions>
        </ContentBlockBody>
      </ContentBlock>
      {isOpen && (
        <ManagementFeeTrueUpsEditDrawer
          fundFamilyId={fundFamilyConfig.fund_family_id}
          fundFamilyConfig={fundFamilyConfig}
          onCloseComplete={onClose}
        />
      )}
    </>
  );
};

const FundFamilyFeeInterestConfigBlock: React.FC<{
  fundFamilyConfig: FundFamilyConfigReadData;
}> = ({ fundFamilyConfig }) => {
  const { t } = useTranslation(["fundFamily", "common"]);
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <>
      <ContentBlock>
        <ContentBlockHeader>
          <ContentBlockTitle>
            {t("fundFamily:config.managementFeeInterestConfig.title")}
          </ContentBlockTitle>
          <Restricted permission={PermissionTypeEnum.config}>
            <ContentBlockActions>
              <ContentBlockMenu>
                <MenuItem onClick={onOpen}>{t("common:edit")}</MenuItem>
              </ContentBlockMenu>
            </ContentBlockActions>
          </Restricted>
        </ContentBlockHeader>

        <ContentBlockBody variant="descriptions">
          <Descriptions>
            <Descriptions.Item
              label={t(
                "fundFamily:setup.profile.advanced.feeAccrualInterestRate",
              )}
            >
              {formatPercentage(
                new Decimal(fundFamilyConfig.fee_accrual_interest_rate).times(
                  100,
                ),
              )}
            </Descriptions.Item>
            <Descriptions.Item
              label={t(
                "fundFamily:config.managementFeeInterestConfig.interest_methodology.title",
              )}
            >
              {t(
                `fundFamily:config.managementFeeInterestConfig.interest_methodology.${fundFamilyConfig.fee_interest_methodology}.title`,
              )}
            </Descriptions.Item>
            <Descriptions.Item
              label={t(
                "fundFamily:config.managementFeeInterestConfig.day_count.title",
              )}
            >
              {t(
                `fundFamily:config.managementFeeInterestConfig.day_count.${fundFamilyConfig.management_fee_interest_day_count}`,
              )}
            </Descriptions.Item>
          </Descriptions>
        </ContentBlockBody>
      </ContentBlock>
      {isOpen && (
        <ManagementFeeInterestEditDrawer
          fundFamilyId={fundFamilyConfig.fund_family_id}
          fundFamilyConfig={fundFamilyConfig}
          onCloseComplete={onClose}
        />
      )}
    </>
  );
};

interface ManagementFeeInterestEditDrawerProps {
  fundFamilyId: string;
  onCloseComplete: () => void;
  fundFamilyConfig: FundFamilyConfigReadData;
}

export const ManagementFeeInterestEditDrawer: React.FC<
  ManagementFeeInterestEditDrawerProps
> = ({ fundFamilyId, onCloseComplete, fundFamilyConfig }) => {
  const { t } = useTranslation(["fundFamily", "enums"]);
  const queryClient = useQueryClient();

  const { mutateAsync: updateFundFamilyConfig } = useFundFamilyConfigUpdate();
  const form = useForm<FundFamilyConfigUpsertDataRequest>({
    resolver: zodResolver(
      fundFamilyConfigUpdateBody.pick({
        fee_interest_methodology: true,
      }),
    ),
    defaultValues: fundFamilyConfig,
  });

  const onSubmit = async (data: FundFamilyConfigUpsertDataRequest) =>
    await updateFundFamilyConfig({
      fundFamilyId,
      data,
    });

  const onSuccess = async () =>
    await queryClient.invalidateQueries({
      queryKey: getFundFamilyConfigRetrieveQueryKey(fundFamilyId),
    });

  return (
    <FormDrawer
      title={t("fundFamily:config.managementFeeInterestConfig.edit.title")}
      formProps={{
        form,
        onSubmit,
        onSuccess,
        successMessage: t(
          "fundFamily:config.managementFeeInterestConfig.edit.edited",
        ),
      }}
      onCloseComplete={onCloseComplete}
    >
      <FormNumberInput
        name="fee_accrual_interest_rate"
        valueType="percent"
        title={t("fundFamily:setup.profile.advanced.feeAccrualInterestRate")}
      />
      <FormSelect
        name="management_fee_interest_day_count"
        title={t(
          "fundFamily:config.managementFeeInterestConfig.day_count.title",
        )}
      >
        {Object.values(InterestDayCountEnum).map((value) => (
          <SelectOption key={value} value={value}>
            {t(`enums:InterestDayCountEnum.${value}`)}
          </SelectOption>
        ))}
      </FormSelect>
      <Text fontSize="16px" fontWeight="semiBold" pb="12px">
        {t(
          "fundFamily:config.managementFeeInterestConfig.interest_methodology.title",
        )}
        :
      </Text>
      <FormRadioGroup variant="card" name="fee_interest_methodology">
        {Object.keys(FeeInterestMethodologyEnum).map((interest_methodology) => (
          <Radio value={interest_methodology} key={interest_methodology}>
            <Text>
              {t(
                `fundFamily:config.managementFeeInterestConfig.interest_methodology.${
                  interest_methodology as "irr"
                }.title`,
              )}
            </Text>
            <Text>
              {t(
                `fundFamily:config.managementFeeInterestConfig.interest_methodology.${
                  interest_methodology as "irr"
                }.description`,
              )}
            </Text>
          </Radio>
        ))}
      </FormRadioGroup>
    </FormDrawer>
  );
};

interface ManagementFeeEditDrawerProps {
  fundFamilyId: string;
  onCloseComplete: () => void;
  fundFamilyConfig: FundFamilyConfigReadData;
}

export const ManagementFeeEditDrawer: React.FC<
  ManagementFeeEditDrawerProps
> = ({ fundFamilyId, onCloseComplete, fundFamilyConfig }) => {
  const { t } = useTranslation(["fundFamily"]);
  const queryClient = useQueryClient();

  const { mutateAsync: updateFundFamilyConfig } = useFundFamilyConfigUpdate();
  const form = useForm<FundFamilyConfigUpsertDataRequest>({
    resolver: zodResolver(
      fundFamilyConfigUpdateBody.pick({
        management_fee_call_methodology: true,
        management_fee_distribution_methodology: true,
      }),
    ),
    defaultValues: fundFamilyConfig,
  });

  const onSubmit = async (data: FundFamilyConfigUpsertDataRequest) =>
    await updateFundFamilyConfig({
      fundFamilyId,
      data,
    });

  const onSuccess = async () =>
    await queryClient.invalidateQueries({
      queryKey: getFundFamilyConfigRetrieveQueryKey(fundFamilyId),
    });

  const distributionMethodologyChoices = Object.keys(
    ManagementFeeDistributionMethodologyEnum,
  );

  return (
    <FormDrawer
      title={t("fundFamily:config.managementFeeConfig.edit.title")}
      formProps={{
        form,
        onSubmit,
        onSuccess,
        successMessage: t("fundFamily:config.managementFeeConfig.edit.edited"),
      }}
      onCloseComplete={onCloseComplete}
    >
      <Text fontSize="16px" fontWeight="semiBold" pb="12px">
        {t("fundFamily:config.managementFeeConfig.capital_call.title")}:
      </Text>
      <FormRadioGroup variant="card" name="management_fee_call_methodology">
        {Object.keys(ManagementFeeCallMethodologyEnum).map(
          (call_methodology) => (
            <Radio value={call_methodology} key={call_methodology}>
              <Text>
                {t(
                  `fundFamily:config.managementFeeConfig.capital_call.${
                    call_methodology as "net_fees"
                  }.title`,
                )}
              </Text>
              <Text>
                {t(
                  `fundFamily:config.managementFeeConfig.capital_call.${
                    call_methodology as "net_fees"
                  }.description`,
                )}
              </Text>
            </Radio>
          ),
        )}
      </FormRadioGroup>
      <Text fontSize="16px" fontWeight="semiBold" mb="12px" mt="16px">
        {t("fundFamily:config.managementFeeConfig.distribution.title")}:
      </Text>
      <FormRadioGroup
        variant="card"
        name="management_fee_distribution_methodology"
      >
        {distributionMethodologyChoices.map((distribution_methodology) => (
          <Radio
            value={distribution_methodology}
            key={distribution_methodology}
          >
            <Text>
              {t(
                `fundFamily:config.managementFeeConfig.distribution.${
                  distribution_methodology as "fee_holdback"
                }.title`,
              )}
            </Text>
            <Text>
              {t(
                `fundFamily:config.managementFeeConfig.distribution.${
                  distribution_methodology as "fee_holdback"
                }.description`,
              )}
            </Text>
          </Radio>
        ))}
      </FormRadioGroup>
    </FormDrawer>
  );
};

const ManagementFeeTrueUpsEditDrawer: React.FC<
  ManagementFeeEditDrawerProps
> = ({ fundFamilyId, onCloseComplete, fundFamilyConfig }) => {
  const { t } = useTranslation(["fundFamily"]);
  const queryClient = useQueryClient();

  const { mutateAsync: updateFundFamilyConfig } = useFundFamilyConfigUpdate();
  const form = useForm<FundFamilyConfigUpsertDataRequest>({
    resolver: zodResolver(
      fundFamilyConfigUpdateBody.pick({
        enable_management_fee_true_ups: true,
      }),
    ),
    defaultValues: fundFamilyConfig,
  });

  const onSubmit = async (data: FundFamilyConfigUpsertDataRequest) =>
    await updateFundFamilyConfig({
      fundFamilyId,
      data,
    });

  const onSuccess = async () =>
    await queryClient.invalidateQueries({
      queryKey: getFundFamilyConfigRetrieveQueryKey(fundFamilyId),
    });

  return (
    <FormDrawer
      title={t("fundFamily:config.managementFeeConfig.true_ups.edit_title")}
      formProps={{
        form,
        onSubmit,
        onSuccess,
        successMessage: t(
          "fundFamily:config.managementFeeConfig.true_ups.edited",
        ),
      }}
      onCloseComplete={onCloseComplete}
    >
      <FormBooleanToggleButtonGroup
        name="enable_management_fee_true_ups"
        title={t("fundFamily:setup.profile.advanced.managementFeeTrueUps")}
      />
    </FormDrawer>
  );
};

interface FeeWaiverEditDrawerProps {
  fundFamilyId: string;
  onCloseComplete: () => void;
}

const FeeWaiverEditDrawer: React.FC<FeeWaiverEditDrawerProps> = ({
  fundFamilyId,
  onCloseComplete,
}) => {
  const { t } = useTranslation(["fundFamily"]);
  const queryClient = useQueryClient();

  const { data: feeWaiverConfig } =
    useFeeWaiverConfigRetrieveSuspense(fundFamilyId);
  const { mutateAsync: updateFeeWaiverConfig } = useFeeWaiverConfigUpdate();
  const { data: transactionCalculations } =
    useTransactionCalculationListSuspense(fundFamilyId);

  const form = useForm<FeeWaiverConfigUpsertDataRequest>({
    resolver: zodResolver(
      feeWaiverConfigUpdateBody.pick({
        is_enabled: true,
        start_date: true,
        fee_waiver_calculation_id: true,
        fee_waiver_cap_calculation_id: true,
        fee_waiver_calculations: true,
      }),
    ),
    defaultValues: feeWaiverConfig,
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "fee_waiver_calculations",
  });

  const onSubmit = async (data: FeeWaiverConfigUpsertDataRequest) =>
    await updateFeeWaiverConfig({
      fundFamilyId,
      data: {
        ...data,
        fee_waiver_calculation_id: data.fee_waiver_calculation_id || null,
        fee_waiver_cap_calculation_id:
          data.fee_waiver_cap_calculation_id || null,
      },
    });

  const onSuccess = async () =>
    await queryClient.invalidateQueries({
      queryKey: getFeeWaiverConfigRetrieveQueryKey(fundFamilyId),
    });

  const transactionCalculationOptions = useMemo(
    () =>
      transactionCalculations.map((calculation) => (
        <SelectOption key={calculation.id} value={calculation.id}>
          {calculation.name}
        </SelectOption>
      )),
    [transactionCalculations],
  );

  // TODO how do you add more inputs dynamically?

  return (
    <FormDrawer
      title={t("fundFamily:config.feeWaiverConfig.edit.title")}
      formProps={{
        form,
        onSubmit,
        onSuccess,
        successMessage: t("fundFamily:config.feeWaiverConfig.edit.edited"),
      }}
      onCloseComplete={onCloseComplete}
    >
      <FormBooleanToggleButtonGroup
        name="is_enabled"
        title={t("fundFamily:config.feeWaiverConfig.isEnabled")}
      />
      <FormDateInput
        name="start_date"
        title={t("fundFamily:config.feeWaiverConfig.startDate")}
      />
      <FormSelect
        name="fee_waiver_cap_calculation_id"
        title={t("fundFamily:config.feeWaiverConfig.capCalculation")}
      >
        {transactionCalculationOptions}
      </FormSelect>
      <FormSelect
        name="fee_waiver_calculation_id"
        title={t("fundFamily:config.feeWaiverConfig.calculation")}
      >
        {transactionCalculationOptions}
      </FormSelect>
      {fields.map((_, index) => (
        <FeeWaiverCalcEntityRelationFormInputs
          key={index}
          index={index}
          fundFamilyId={fundFamilyId}
          remove={() => remove(index)}
        />
      ))}
      <Button
        colorScheme="secondary"
        size="sm"
        iconSpacing="2px"
        leftIcon={<SmallAddIcon />}
        onClick={() => append(addEmptyFeeWaiverCalc())}
      >
        {t("fundFamily:config.feeWaiverConfig.addCalculation")}
      </Button>
    </FormDrawer>
  );
};

const FeeWaiverCalcEntityRelationFormInputs: React.FC<{
  index: number;
  fundFamilyId: string;
  remove: () => void;
}> = ({ index, fundFamilyId, remove }) => {
  const { t } = useTranslation(["common"]);
  return (
    <FormFieldPrefix prefix={`fee_waiver_calculations.${index}`}>
      <FormFieldset border="1px dashed" borderColor="gray.400">
        <FormFundEntitySelect
          name="fund_entity_id"
          title={t("common:entity")}
          fundFamilyId={fundFamilyId}
        />
        <TransactionCalculationSelect
          fundFamilyId={fundFamilyId}
          name="fee_waiver_calculation_id"
        />
        <HStack justifyContent="flex-end">
          <Link size="sm" colorScheme="gray" onClick={() => remove()}>
            {t("common:remove")}
          </Link>
        </HStack>
      </FormFieldset>
    </FormFieldPrefix>
  );
};

const addEmptyFeeWaiverCalc =
  (): FeeWaiverCalculationFundEntityRelationUpsertData => ({
    fund_entity_id: "",
    fee_waiver_calculation_id: "",
  });
